import {
  AlertTriangle,
  Archive,
  CheckCircle2,
  ChevronDown,
  Cloud,
  Download,
  FileArchive,
  FlaskConical,
  HardDrive,
  Loader2,
  RotateCcw,
  Trash2,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ActionMenu } from '@/modules/backups/ActionMenu';
import { TrustBadge } from '@/modules/backups/badges';
import { exactTime, humanSize, shortDateTime } from '@/modules/backups/format';
import { latestSuccessfulIds } from '@/modules/backups/health';
import { divider, hairline, quietButton, toneWash } from '@/components/surface/tones';
import { Panel, Pill, SectionHeader, StatusGlyph } from '@/components/surface/ui';
import { humanDuration, relativeTime } from '@/modules/monitoring/format';
import type { BackupDestination, BackupRecord } from '@/types/api';

/** Backups shown before the history is opened: the latest few, enough to restore from. */
const LATEST_ROWS = 5;

/**
 * The column template, shared by the header and every row so the two can never drift apart:
 * status · source · verification · destination · when · actions.
 */
const COLUMNS = 'xl:grid-cols-[1.25rem_minmax(0,1fr)_9rem_10rem_8rem_14.5rem] xl:gap-x-5';

export interface PendingRun {
  source: string;
  destination: string;
}

/**
 * The record of backups taken: the page's audit trail, at the foot of the page.
 *
 * ## The latest few are always there; the rest is one click away
 *
 * The page is for how things stand now — health, verification, schedules, storage. The history is
 * what somebody opens to restore from or to check what happened, so the newest five stay visible
 * without opening anything and the rest waits behind "View all history".
 *
 * ## Built to be scanned
 *
 * On a wide screen every row lines up in the same columns as the header above it. The source name
 * is the only thing in the heavier weight; what it contains, where it went and when sit in
 * smaller, quieter text beside it; and the actions occupy the same fixed column on every row, so
 * Restore is always in the same place. Each source's newest successful backup is marked
 * **Latest**, because that is the copy almost every restore starts from.
 *
 * ## A failed backup stays in the list
 *
 * "The last three nights failed" is the most important thing this page can say, so a failure is a
 * row like any other with its reason under it — and the health panel above links here when one is
 * the latest backup of a source.
 *
 * Every button keeps the rule it always had: nothing can be restored, tested or downloaded until the
 * backup has completed, and paid actions wait for an active subscription. A backup whose source has
 * been removed can still be downloaded, but not restored or tested: the platform no longer knows where
 * its data belongs, and the row says so.
 */
export function BackupActivity({
  backups,
  destinations,
  pendingRun,
  permitsChanges,
  canDownload,
  testingSources,
  busy,
  expanded,
  onExpandedChange,
  onDownload,
  onTestRestore,
  onRestore,
  onForget,
}: {
  backups: BackupRecord[];
  destinations: BackupDestination[];
  pendingRun: PendingRun | null;
  permitsChanges: boolean;
  /** False on a plan without downloads through the platform; the items then say why. */
  canDownload: boolean;
  testingSources: Set<string>;
  busy: { download: boolean; testRestore: boolean; forget: boolean };
  /** Whether the older history is showing. Held by the page so the health panel can open it. */
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  onDownload: (id: string, format: 'tar' | 'zip') => void;
  onTestRestore: (id: string) => void;
  onRestore: (backup: BackupRecord) => void;
  onForget: (id: string) => void;
}) {
  const offsiteById = new Map(destinations.map((d) => [d.id, d.is_offsite]));
  const latest = latestSuccessfulIds(backups);
  const visible = expanded ? backups : backups.slice(0, LATEST_ROWS);
  const older = backups.length - LATEST_ROWS;
  const failed = backups.filter((backup) => backup.status === 'failed').length;

  return (
    <Panel id="activity">
      <SectionHeader
        icon={Archive}
        iconTone="muted"
        title="Backup history"
        badge={
          backups.length > 0 ? (
            <span className="text-xs tabular-nums text-muted-foreground">
              {backups.length} recent
              {failed > 0 && <span className="text-danger"> · {failed} failed</span>}
            </span>
          ) : undefined
        }
        description="Recent backups, including failed attempts."
      />

      {pendingRun !== null && (
        <div className={cn('mx-3 mb-2 flex items-center gap-3 rounded-lg px-3 py-2.5', toneWash.primary)} role="status">
          <StatusGlyph icon={Loader2} tone="primary" spin label="In progress" size="sm" />
          <div className="min-w-0">
            <p className="text-sm font-medium">
              Backing up {pendingRun.source}
              <span className="font-normal text-muted-foreground"> to {pendingRun.destination}</span>
            </p>
            <p className="text-xs text-muted-foreground">This can take a few minutes.</p>
          </div>
        </div>
      )}

      {backups.length === 0 && pendingRun === null ? (
        <p className="px-5 pb-5 text-[13px] text-muted-foreground">
          No backups yet. Take one with <span className="font-medium text-foreground">Back up now</span>, or
          add a schedule and the first runs on its own.
        </p>
      ) : (
        <>
          <div
            aria-hidden
            className={cn(
              'hidden border-y px-5 py-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground xl:grid',
              COLUMNS,
              hairline,
            )}
          >
            <span />
            <span>Source</span>
            <span>Verification</span>
            <span>Destination</span>
            <span>When</span>
            <span />
          </div>

          <ul className={cn('divide-y border-t xl:border-t-0', divider, hairline)}>
            {visible.map((backup) => (
              <BackupRow
                key={backup.id}
                backup={backup}
                latest={latest.has(backup.id)}
                offsite={offsiteById.get(backup.destination_id)}
                permitsChanges={permitsChanges}
                canDownload={canDownload}
                testing={testingSources.has(backup.source_id)}
                busy={busy}
                onDownload={onDownload}
                onTestRestore={onTestRestore}
                onRestore={onRestore}
                onForget={onForget}
              />
            ))}
          </ul>

          {older > 0 && (
            <button
              type="button"
              onClick={() => onExpandedChange(!expanded)}
              aria-expanded={expanded}
              className={cn(
                'flex w-full items-center justify-center gap-1.5 rounded-b-xl border-t px-5 py-3 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-slate-900/[0.03] hover:text-foreground dark:hover:bg-white/[0.03]',
                hairline,
              )}
            >
              <ChevronDown className={cn('size-4 transition-transform', expanded && 'rotate-180')} aria-hidden />
              {expanded ? 'Show latest only' : 'View all history'}
              {!expanded && (
                <span className="font-normal text-muted-foreground">
                  · {older} older {older === 1 ? 'backup' : 'backups'}
                </span>
              )}
            </button>
          )}
        </>
      )}
    </Panel>
  );
}

function BackupRow({
  backup,
  latest,
  offsite,
  permitsChanges,
  canDownload,
  testing,
  busy,
  onDownload,
  onTestRestore,
  onRestore,
  onForget,
}: {
  backup: BackupRecord;
  /** The newest successful backup of its source. */
  latest: boolean;
  /** Undefined when the destination has since been removed. */
  offsite: boolean | undefined;
  permitsChanges: boolean;
  testing: boolean;
  busy: { download: boolean; testRestore: boolean; forget: boolean };
  /** False on a plan that does not include pulling an archive through the platform. */
  canDownload: boolean;
  onDownload: (id: string, format: 'tar' | 'zip') => void;
  onTestRestore: (id: string) => void;
  onRestore: (backup: BackupRecord) => void;
  onForget: (id: string) => void;
}) {
  const succeeded = backup.status === 'succeeded';
  // Said once, used by both download items: the archive is not withheld, only the convenience of
  // fetching it from here, so the hint says where it actually is.
  const downloadHint = canDownload
    ? undefined
    : 'Your plan does not include downloading through Techie Gain. The archive is in your own storage, or on the server it was taken from.';
  const status =
    backup.status === 'succeeded'
      ? { icon: CheckCircle2, className: 'text-success', spin: false }
      : backup.status === 'failed'
        ? { icon: XCircle, className: 'text-danger', spin: false }
        : { icon: Loader2, className: 'text-primary', spin: true };
  const StatusIcon = status.icon;

  const contents =
    backup.scope === 'database' ? 'Database only' : backup.included_database ? 'Files + database' : 'Files';

  const destination = (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      {offsite === true && <Cloud className="size-3.5 shrink-0 opacity-70" aria-hidden />}
      {offsite === false && <HardDrive className="size-3.5 shrink-0 opacity-70" aria-hidden />}
      <span className="truncate">{backup.destination_name}</span>
    </span>
  );

  /** Verification, or why there is none: the state a failed or running backup is in. */
  const verification = succeeded ? (
    <TrustBadge trust={backup.trust} />
  ) : (
    <span className={cn('text-xs font-medium', backup.status === 'failed' ? 'text-danger' : 'text-primary')}>
      {backup.status_label}
    </span>
  );

  return (
    <li className="px-5 py-3.5 transition-colors last:rounded-b-xl hover:bg-slate-900/[0.02] dark:hover:bg-white/[0.02]">
      <div
        className={cn(
          'grid grid-cols-[1.25rem_minmax(0,1fr)] items-start gap-x-3 gap-y-2.5 sm:grid-cols-[1.25rem_minmax(0,1fr)_auto] xl:items-center',
          COLUMNS,
        )}
      >
        <StatusIcon
          className={cn('mt-0.5 size-5 xl:mt-0', status.className, status.spin && 'animate-spin')}
          aria-label={backup.status_label}
          role="img"
        />

        {/* Source: the one thing in the heavier weight. */}
        <div className="min-w-0">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <span className="truncate text-sm font-semibold text-foreground">{backup.source_name}</span>
            {latest && <Pill tone="primary">Latest</Pill>}
            {!backup.source_exists && (
              <Pill tone="muted" title="This source has been removed. Its backups can be downloaded, but not restored or tested here.">
                Source removed
              </Pill>
            )}
            <span className="xl:hidden">{succeeded ? <TrustBadge trust={backup.trust} /> : verification}</span>
          </div>
          <p className="mt-0.5 truncate text-xs text-muted-foreground" title={`${contents} · ${backup.manual ? 'Manual' : 'Scheduled'}`}>
            {contents} · {backup.manual ? 'Manual' : 'Scheduled'}
            {backup.duration_seconds !== null && ` · took ${humanDuration(backup.duration_seconds)}`}
          </p>
          {/* Below the widest layout the other columns fold into one quiet line. */}
          <p className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground xl:hidden">
            {destination}
            {succeeded && (
              <>
                <span aria-hidden>·</span>
                <span className="tabular-nums">{humanSize(backup.size_bytes)}</span>
              </>
            )}
            <span aria-hidden>·</span>
            <time dateTime={backup.started_at} title={exactTime(backup.started_at)}>
              {relativeTime(backup.started_at)}
            </time>
          </p>
        </div>

        <div className="hidden xl:block">
          {succeeded && backup.trust === 'unverified' ? <span className="text-xs text-muted-foreground">Not tested yet</span> : verification}
        </div>

        <div className="hidden min-w-0 xl:block">
          <p className="truncate text-[13px] text-foreground/85">{destination}</p>
          {succeeded && <p className="text-xs tabular-nums text-muted-foreground">{humanSize(backup.size_bytes)}</p>}
        </div>

        <div className="hidden xl:block">
          <p className="text-[13px] text-foreground/85">
            <time dateTime={backup.started_at} title={exactTime(backup.started_at)}>
              {relativeTime(backup.started_at)}
            </time>
          </p>
          <p className="text-xs text-muted-foreground">{shortDateTime(backup.started_at)}</p>
        </div>

        <div className="col-start-2 -ml-2.5 flex items-center gap-1 sm:col-start-auto sm:ml-0 sm:justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onTestRestore(backup.id)}
            disabled={!permitsChanges || !backup.is_restorable || !backup.source_exists || busy.testRestore || testing}
            title={
              !backup.source_exists
                ? 'The source this backup belongs to has been removed, so it cannot be tested here'
                : testing
                  ? 'A restore test of this source is running'
                  : 'Prove this backup restores: it is fetched back and checked, and its database is loaded into a scratch copy and read back. Your live data is not touched.'
            }
            className="text-muted-foreground hover:bg-slate-900/[0.05] hover:text-foreground dark:hover:bg-white/[0.06]"
          >
            {testing ? <Loader2 className="animate-spin" /> : <FlaskConical />}
            Test restore
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRestore(backup)}
            disabled={!permitsChanges || !backup.is_restorable || !backup.source_exists}
            title={
              !backup.source_exists
                ? 'The source this backup belongs to has been removed, so the platform no longer knows where to restore it. Download it instead.'
                : backup.is_restorable
                  ? 'Restore this backup over the live data'
                  : 'Only a completed backup can be restored'
            }
            className={quietButton}
          >
            <RotateCcw />
            Restore
          </Button>
          <ActionMenu
            label={`More actions for the ${backup.source_name} backup`}
            items={[
              {
                label: 'Download .tar.gz',
                icon: Download,
                hint: downloadHint,
                title: 'Download the archive as .tar.gz',
                disabled: !canDownload || !backup.is_restorable || busy.download,
                onSelect: () => onDownload(backup.id, 'tar'),
              },
              {
                label: 'Download .zip',
                icon: FileArchive,
                hint: downloadHint ?? 'Converted on your server first. Needs free disk space there.',
                title: 'Convert to .zip on your server, then download. Needs free disk space there.',
                disabled: !canDownload || !backup.is_restorable || busy.download,
                onSelect: () => onDownload(backup.id, 'zip'),
              },
              {
                label: 'Forget this backup',
                icon: Trash2,
                hint: 'The archive itself is not deleted.',
                title: 'Forget this backup. The archive itself is not deleted.',
                tone: 'danger',
                separated: true,
                disabled: busy.forget,
                onSelect: () => onForget(backup.id),
              },
            ]}
          />
        </div>

        {backup.last_error !== '' && (
          /*
           * A succeeded backup can still carry a message: retention may have been unable to delete
           * it from storage. That copy is fine and still restorable, so showing it in destructive
           * red beside a completed badge would say the wrong thing. Amber for "there is something
           * you should deal with", red for "this backup did not work".
           */
          <p
            className={cn(
              // Start and end lines rather than a span: `col-span` resets the start, and the message
              // would slide under the status icon instead of lining up with the source name.
              'col-start-2 col-end-[-1] flex items-start gap-2 rounded-lg px-3 py-2 text-xs',
              succeeded ? toneWash.warning : toneWash.danger,
            )}
          >
            <AlertTriangle className={cn('mt-px size-3.5 shrink-0', succeeded ? 'text-warning' : 'text-danger')} aria-hidden />
            <span className="min-w-0 break-words text-foreground/90">{backup.last_error}</span>
          </p>
        )}
      </div>
    </li>
  );
}
